export const APP_LANGUAGES = [
  {
    id: 'de-DE',
    nativeLabel: 'Deutsch',
  },
  {
    id: 'en-US',
    nativeLabel: 'English (US)',
  },
] as const;

export type AppLanguage = (typeof APP_LANGUAGES)[number]['id'];

export const DEFAULT_LANGUAGE: AppLanguage = 'de-DE';
export const FALLBACK_LANGUAGE: AppLanguage = 'de-DE';
export const LANGUAGE_STORAGE_KEY = 'direct-booking-language';

/**
 * A constant array representing the scopes for different booking-related routes in the system.
 * Each scope corresponds to a specific functionality or step within the booking process.
 * Note: May be used to load multiple scoped translation files for different booking steps at once.
 * @example ```
 *  export const routes: Routes = [
 *   {
 *     path: 'booking/property-selection',
 *     loadComponent: () =>
 *       import('./components/booking/property-selection').then((m) => m.PropertySelectionComponent),
 *     providers: [...provideTranslocoScope(...BOOKING_ROUTE_SCOPES)],
 *   },
 * ];
 * ```
 */
export const BOOKING_ROUTE_SCOPES = [
  'booking-common',
  'booking-stepper',
  'booking-select-property',
  'booking-service-provider',
  'booking-property-owner',
  'booking-poa-owner-to-provider',
  'booking-poa-provider-to-docestate',
  'booking-inheritance',
  'booking-checkout',
] as const;

/**
 * Checks if the given value is a valid application language.
 *
 * @param {string} value - The value to check.
 * @return {boolean} Returns `true` if the value is a valid application language, otherwise `false`.
 */
export function isAppLanguage(value: string): value is AppLanguage {
  return APP_LANGUAGES.some((language) => language.id === value);
}

/**
 * Normalizes a given language string to a standardized application language format.
 *
 * @param {string | null | undefined} value - The input language value to normalize. Can be a string, null, or undefined.
 * @return {AppLanguage} The normalized language value. Defaults to the application's default language if the input is null, undefined, or invalid.
 */
export function normalizeLanguage(value: string | null | undefined): AppLanguage {
  if (!value) {
    return DEFAULT_LANGUAGE;
  }

  const normalized = value.trim();
  if (isAppLanguage(normalized)) {
    return normalized;
  }

  if (normalized.startsWith('de')) {
    return 'de-DE';
  }

  if (normalized.startsWith('en')) {
    return 'en-US';
  }

  return DEFAULT_LANGUAGE;
}
